import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  {
    key: "month",
    q: "What month would you like to go?",
    options: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    grid: "grid-cols-3 sm:grid-cols-4",
  },
  {
    key: "days",
    q: "How many days would you like to stay?",
    options: ["1 day", "2–3 days", "7 days"],
    grid: "grid-cols-1",
  },
  {
    key: "guests",
    q: "How many guests will be joining?",
    options: ["Just me", "2 guests", "3–4 guests", "5+ guests"],
    grid: "grid-cols-2",
  },
];

export const BookingQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const total = STEPS.length;
  const isDetails = step === total;
  const progress = result ? 100 : Math.round((step / (total + 1)) * 100);

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/booking/inquiry", { ...form, ...answers });
      setResult(data);
      toast.success("Inquiry received!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="plan" className="py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-clay mb-4">
            <CalendarCheck size={14} /> Plan your stay
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink">
            Let's plan your retreat
          </h2>
          <p className="mt-5 text-muted">Three quick questions and we'll tailor the perfect stay for you.</p>
        </div>

        <div className="bg-forest text-sand rounded-[2rem] p-7 sm:p-12 min-h-[24rem] flex flex-col">
          <div className="mb-10">
            <div className="h-[3px] bg-sand/20 rounded-full overflow-hidden">
              <motion.div className="h-full bg-dune" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!result && !isDetails && (
              <motion.div
                key={`bq-${step}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1 flex flex-col"
              >
                <span className="text-sm text-dune mb-4">
                  {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl mb-8 leading-snug">{STEPS[step].q}</h3>
                <div className={`grid ${STEPS[step].grid} gap-3`}>
                  {STEPS[step].options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`booking-option-${STEPS[step].key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => choose(STEPS[step].key, opt)}
                      className={`px-6 py-4 rounded-xl border text-center transition-all ${
                        answers[STEPS[step].key] === opt
                          ? "border-dune bg-sand text-forest"
                          : "border-sand/30 hover:border-dune hover:bg-sand/10"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="booking-back-btn"
                    className="mt-8 inline-flex items-center gap-2 text-sm text-dune hover:text-sand self-start"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </motion.div>
            )}

            {!result && isDetails && (
              <motion.div
                key="bq-details"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45 }}
                className="flex-1 flex flex-col"
              >
                <h3 className="font-serif text-2xl sm:text-3xl mb-3 leading-snug">Where shall we send your plan?</h3>
                <p className="text-sand/70 mb-8">
                  {answers.month} · {answers.days} · {answers.guests}
                </p>
                <div className="space-y-6">
                  <input
                    data-testid="booking-name-input"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-sand/30 py-3 text-lg placeholder:text-sand/50 focus:outline-none focus:border-dune transition-colors"
                  />
                  <input
                    data-testid="booking-email-input"
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-b border-sand/30 py-3 text-lg placeholder:text-sand/50 focus:outline-none focus:border-dune transition-colors"
                  />
                </div>
                <div className="mt-10 flex items-center gap-4">
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="booking-details-back-btn"
                    className="inline-flex items-center gap-2 text-sm text-dune hover:text-sand"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={loading}
                    data-testid="booking-submit-btn"
                    className="ml-auto rounded-full bg-sand text-forest px-8 py-4 text-sm hover:bg-dune transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send my plan <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {result && (
              <motion.div
                key="bq-result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex-1 flex flex-col justify-center"
                data-testid="booking-result"
              >
                <CalendarCheck size={32} className="text-dune mb-4" />
                <h3 className="font-serif text-3xl leading-tight">Your plan is on its way, {form.name.split(" ")[0]}.</h3>
                <p className="mt-4 text-sand/80 leading-relaxed">
                  We've noted <span className="text-dune">{answers.month}</span>, a{" "}
                  <span className="text-dune">{answers.days}</span> stay for{" "}
                  <span className="text-dune">{answers.guests.toLowerCase()}</span>. Based on this, we recommend the{" "}
                  <span className="font-serif text-dune">{result.recommended_package.name}</span>.
                </p>
                <a
                  href="#retreats"
                  data-testid="booking-view-retreat-btn"
                  className="mt-8 inline-flex items-center gap-2 self-start rounded-full bg-sand text-forest px-7 py-3.5 text-sm hover:bg-dune transition-colors"
                >
                  View {result.recommended_package.name} <ArrowRight size={16} />
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
